import axios, { AxiosInstance } from 'axios';
import {
  AuthToken,
  AvailableSessionDefinitions,
  Hardware,
  LoginRequest,
  Program,
  RegisterRequest,
  Session,
  SessionDataCompiled,
  SocialLoginRequest,
  User,
  UserDetailed,
} from '../types/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = String(error.config?.url ?? '');
        const isPasswordResetCall =
          url.includes('/auth/password/request-reset') ||
          url.includes('/auth/password/validate-reset') ||
          url.includes('/auth/password/reset');
        if (error.response?.status === 401 && !isPasswordResetCall) {
          // Token expired or invalid
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Health Check
  async healthCheck(): Promise<{ status: string; message: string; timestamp: string }> {
    const response = await this.api.get('/');
    return response.data;
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<{ user: User; token: AuthToken }> {
    const response = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async register(userData: RegisterRequest): Promise<{ user: User; token: AuthToken }> {
    const response = await this.api.post('/auth/register', userData);
    return response.data;
  }

  async getProfile(): Promise<{ profile: UserDetailed }> {
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  async updateProfile(
    fullName?: string,
    email?: string,
    avatarUrl?: string,
    dateOfBirth?: string,
    gender?: string,
    phoneNumber?: string,
    address_street?: string,
    address_street2?: string,
    address_city?: string,
    address_state?: string,
    address_postalCode?: string,
    address_country?: string,
    timezone?: string,
  ): Promise<{ message: string }> {
    const response = await this.api.put('/auth/profile', {
      fullName,
      email,
      avatarUrl,
      dateOfBirth,
      gender,
      phoneNumber,
      address_street,
      address_street2,
      address_city,
      address_state,
      address_postalCode,
      address_country,
      timezone,
    });
    return response.data;
  }

  async updatePassword(
    oldPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<{ message: string }> {
    const response = await this.api.post('/auth/password', { oldPassword, newPassword, confirmPassword });
    return response.data;
  }

  async validateResetToken(
    email: string,
    token: string
  ): Promise<{ token: { type: string,value: string } }> {
    const response = await this.api.post('/auth/password/validate-reset', { email, token });
    return response.data;
  }

  async requestPasswordReset(email: string): Promise<{ message: string, expiresAt: string }> {
    const response = await this.api.post('/auth/password/request-reset', { email });
    return response.data;
  }

  async resetPassword(
    resetBearerToken: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<{ message: string }> {
    const response = await this.api.post(
      '/auth/password/reset',
      { newPassword, confirmPassword },
      {
        headers: {
          Authorization: `Bearer ${resetBearerToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Google OAuth: send the Google ID token (credential) to the backend.
   * Contract: POST /auth/google with body { credential: string }.
   * Expected response: { user: User; token: AuthToken } (same as login).
   * Backend should verify the credential with Google and create/return app session.
   */
  async googleAuth(credential: string): Promise<{ user: User; token: AuthToken }> {
    const response = await this.api.post('/auth/google', { credential });
    return response.data;
  }

  /**
   * GET /auth/google/callback is reserved for server-side redirect flow:
   * Backend may redirect users to /auth/google, then Google redirects to
   * /auth/google/callback?code=... for the backend to exchange the code
   * and establish session. Not called directly from this client when using
   * the popup/one-tap flow (we use POST /auth/google with credential instead).
   */

  async socialLogin(socialData: SocialLoginRequest): Promise<{ user: User; token: AuthToken }> {
    const response = await this.api.post('/auth/social/login', socialData);
    return response.data;
  }

  async socialRegister(socialData: SocialLoginRequest): Promise<{ user: User; token: AuthToken; message: string }> {
    const response = await this.api.post('/auth/social/register', socialData);
    return response.data;
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
  }

  async getCurrentUser(): Promise<{ user: UserDetailed }> {
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  // Hardware Pairing
  async startHardwarePairing(): Promise<{
    pairingCode: string;
    expiresAt?: string;
    paired?: boolean
  }> {
    const response = await this.api.get('/hardware/pair');
    return response.data;
  }

  async completeHardwarePairing(systemIdentifier: string, hardwareId: string): Promise<void> {
    await this.api.post('/hardware/pair/complete', {
      systemIdentifier,
      hardwareId,
    });
  }

  async checkHeadsetPairingStatus(systemIdentifier: string): Promise<{ paired: boolean }> {
    const response = await this.api.get(`/headset-configurations/${systemIdentifier}/paired`);
    return response.data;
  }

  // Sessions
  async getAvailableSessions(): Promise<AvailableSessionDefinitions> {
    const response = await this.api.get('/sessions/available');
    return response.data;
  }

  async getSessions(params?: {
    sessionDefinitionId?: string;
    userId?: string;
    providerId?: string;
    programId?: string;
    contentType?: 'hardware' | 'software' | 'services';
    contentId?: string;
    status?: 'active' | 'upcoming' | 'completed' | 'incomplete' | 'in-progress' | 'all';
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{ sessions: Session[]; meta: any }> {
    const response = await this.api.get('/sessions', { params });
    return response.data;
  }

  async getSession(id: string): Promise<{ session: Session }> {
    const response = await this.api.get(`/sessions/${id}`);
    return response.data;
  }

  async updateSessionMetadata(
    id: string,
    payload: { metadata: Record<string, any> }
  ): Promise<{ session: Session }> {
    const response = await this.api.put(`/sessions/${id}`, payload);
    return response.data;
  }

  async getSessionData(id: string, refresh?: boolean): Promise<SessionDataCompiled | undefined> {
    const params = refresh ? { refresh: true } : undefined;
    const response = await this.api.get(`/sessions/${id}/data`, { params } );
    return response.data;
  }

  async createSession(sessionData: {
    sessionDefinitionId: string;
    userId?: string;
    providerId?: string;
    programId?: string;
    content: Array<{ contentId: string; contentType: 'hardware' | 'software' | 'services'; ver?: string }>;
    start: string;
    end?: string;
  }): Promise<{ session: Session }> {
    const response = await this.api.post('/sessions', sessionData);
    return response.data;
  }

  async createSessionCode(sessionData: {
    sessionDefinitionId?: string;
    providerId?: string;
    programId?: string;
    enrollmentId?: string;
    userId?: string;
    nameOverride?: string;
    instructionsOverride?: string;
  }): Promise<{
    sessionCode: string;
    expiresIn: number;
  }> {
    const response = await this.api.post('/sessions/code', sessionData);
    return response.data;
  }

  // Hardware
  async getHardware(params?: {
    page?: number;
    limit?: number;
  }): Promise<{ hardware: Hardware[]; meta: any }> {
    const response = await this.api.get('/hardware', { params });
    return response.data;
  }

  async getHardwareById(id: string): Promise<{ hardware: Hardware }> {
    const response = await this.api.get(`/hardware/${id}`);
    return response.data;
  }

  // Programs
  async getPrograms(): Promise<{ programs: Program[] }> {
    const response = await this.api.get('/programs');
    return response.data;
  }

  async getProgram(id: string): Promise<{ program: Program }> {
    const response = await this.api.get(`/programs/${id}`);
    return response.data;
  }
}

export const apiService = new ApiService();
